import {
  Star, Calendar, Clock, Plane, Anchor, Globe, Shield, ChevronRight, ChevronLeft,
  Wifi, Zap, Smartphone, MapPin, Users, Award, Send, Check, MessageCircle, Heart
} from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { useFeaturedPackages } from '../hooks/useFeaturedPackages'
import Newsletter from '../components/Newsletter'
import { useEffect, useState, useRef } from 'react'
import JsonLd from '../components/JsonLd'
import { BRAND } from '../theme/brand'

// Vibrant Unsplash hero images for destinations 2Life actually serves
const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=1920&q=80', // Maldives overwater
  'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1920&q=80', // Safari sunset
  'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1920&q=80', // Bali temple
  'https://images.unsplash.com/photo-1602002418816-5c0aeef426aa?w=1920&q=80', // Thailand longtail
  'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=1920&q=80', // Cape Town
]

// Real Google reviews
const REVIEWS = [
  {
    name: 'Satisfied Traveller',
    date: '2024',
    rating: 5,
    quote: 'Adriaan went above and beyond to plan our dream trip to Thailand. Every detail was taken care of — flights, transfers, hotels, even restaurant recommendations. Truly a 5-star experience from start to finish.',
  },
  {
    name: 'Happy Client',
    date: '2024',
    rating: 5,
    quote: 'Our family safari in KwaZulu-Natal was perfectly organised. The kids loved every moment and we didn\'t have to worry about a thing. 2Life Travel made it effortless.',
  },
  {
    name: 'Repeat Customer',
    date: '2025',
    rating: 5,
    quote: 'This is our third trip booked through 2Life Travel and they never disappoint. The Mauritius package was incredible value with beautiful beachfront accommodation.',
  },
]

// Scroll animation hook
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true) },
      { threshold }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [threshold])

  return { ref, isVisible }
}

// Animated section wrapper
function AnimateIn({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView()
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  )
}

export default function Home() {
  const { franchiseData, loading } = useFranchiseData()
  const { featuredPackages, loading: packagesLoading, error: packagesError } = useFeaturedPackages()

  const [heroIndex, setHeroIndex] = useState(0)
  const [heroLoaded, setHeroLoaded] = useState(false)
  const [activeReview, setActiveReview] = useState(0)
  const [videoLoaded, setVideoLoaded] = useState(false)

  // Hero slideshow fallback (if video doesn't load)
  useEffect(() => {
    const timeout = setTimeout(() => setHeroLoaded(true), 300)
    return () => clearTimeout(timeout)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % HERO_IMAGES.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  // Reviews auto-rotate
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveReview((prev) => (prev + 1) % REVIEWS.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [])

  if (loading || !franchiseData) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <img src="/2life-logo.png" alt="2Life Travel" className="h-20 w-auto mx-auto mb-6 opacity-50" />
          <div className="h-1 w-32 mx-auto rounded-full overflow-hidden bg-gray-200">
            <div className="h-full rounded-full animate-pulse" style={{ backgroundColor: BRAND.maroon, width: '60%' }}></div>
          </div>
        </div>
      </div>
    )
  }

  const brandName = franchiseData?.name || '2Life Travel'
  const accentColor = BRAND.maroon

  const getImageUrl = (pkg: any) => {
    if (pkg.cover_photo?.large_url) return pkg.cover_photo.large_url
    if (pkg.cover_photo?.url) return pkg.cover_photo.url
    if (pkg.scraped_cover_photo) return pkg.scraped_cover_photo
    return null
  }

  return (
    <div className="bg-white">
      <Helmet>
        <title>{brandName} | Give Your Dreams Wings</title>
        <meta name="description" content="Premium personalized travel experiences. Tailormade itineraries, luxury holidays, river and ocean cruises and more. Give your dreams wings with 2Life Travel." />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.origin + '/' : '/'} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${brandName} | Give Your Dreams Wings`} />
        <meta property="og:description" content="Premium personalized travel experiences. Tailormade itineraries, luxury holidays, river and ocean cruises and more." />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : '/'} />
        <meta property="og:image" content="/2life-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Home',
          url: typeof window !== 'undefined' ? window.location.href : '/',
          description: "Premium personalized travel experiences by 2Life Travel.",
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: typeof window !== 'undefined' ? window.location.origin + '/' : '/' }],
          },
        }}
      />

      {/* ==================== VIDEO HERO ==================== */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video background — replace src with your own MP4 in public/videos/ */}
        <video
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={() => setVideoLoaded(true)}
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: 'cover', objectPosition: 'center', opacity: videoLoaded ? 1 : 0, transition: 'opacity 1s' }}
          poster={HERO_IMAGES[0]}
        >
          {/* Add your video file here. Download a free one from pexels.com/videos or coverr.co */}
          { <source src="/videos/hero-travel.mp4" type="video/mp4" /> }
        </video>

        {/* Image slideshow fallback (shows when video not available) */}
        {!videoLoaded && HERO_IMAGES.map((img, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-[2000ms]"
            style={{
              backgroundImage: `url(${img})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: heroIndex === i ? 1 : 0,
            }}
          />
        ))}

        {/* Gradient overlay */}
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(135deg, rgba(26,26,26,0.28) 0%, rgba(122,27,62,0.18) 50%, rgba(26,26,26,0.25) 100%)` }}
        />

        {/* Gold lines */}
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: `linear-gradient(90deg, transparent, ${BRAND.gold}, transparent)` }} />
        <div className="absolute bottom-0 left-0 w-full h-1" style={{ background: `linear-gradient(90deg, transparent, ${BRAND.gold}, transparent)` }} />

        {/* Hero content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div
            className="mb-6 transition-all duration-1000"
            style={{ opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? 'translateY(0)' : 'translateY(-20px)' }}
          >
            <svg width="40" height="40" viewBox="0 0 40 40" className="mx-auto" style={{ opacity: 0.8 }}>
              <path d="M20 0 L22 16 L40 20 L22 22 L20 40 L18 22 L0 20 L18 16 Z" fill={BRAND.gold} />
            </svg>
          </div>

          <p
            className="inline-block text-sm tracking-[0.35em] uppercase mb-6 px-4 py-2 transition-all duration-1000 delay-200"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              color: '#FFFFFF',
              background: 'rgba(122,27,62,0.72)',
              border: `1px solid ${BRAND.gold}`,
              boxShadow: '0 4px 18px rgba(0, 0, 0, 0.35)',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
              opacity: heroLoaded ? 1 : 0,
              transform: heroLoaded ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            Premium Travel Experiences
          </p>

          <h1
            className="text-5xl sm:text-7xl lg:text-8xl font-light text-white leading-[1.1] mb-4 transition-all duration-1000 delay-300"
            style={{ fontFamily: "'Cormorant Garamond', serif", opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? 'translateY(0)' : 'translateY(30px)' }}
          >
            Give Your<br />
            <span className="italic font-medium" style={{ color: BRAND.gold }}>Dreams</span> Wings
          </h1>

          <p
            className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed transition-all duration-1000 delay-500"
            style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300, letterSpacing: '0.02em', opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? 'translateY(0)' : 'translateY(20px)' }}
          >
            Bespoke travel itineraries crafted by Pretoria's<br className="hidden sm:block" />
            most passionate travel experts
          </p>

          <div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-1000 delay-700"
            style={{ opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? 'translateY(0)' : 'translateY(20px)' }}
          >
            <a
              href="/contact"
              className="group inline-flex items-center gap-3 px-10 py-4 text-white text-sm font-medium tracking-wider uppercase transition-all duration-300 hover:scale-105"
              style={{ fontFamily: "'Montserrat', sans-serif", backgroundColor: BRAND.maroon, borderRadius: '2px' }}
            >
              Plan Your Journey <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="/packages"
              className="inline-flex items-center gap-3 px-10 py-4 text-sm font-medium tracking-wider uppercase transition-all duration-300 border hover:bg-white/10"
              style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.gold, borderColor: BRAND.gold, borderRadius: '2px' }}
            >
              View Packages
            </a>
          </div>

          {/* Slide indicators */}
          {!videoLoaded && (
            <div className="mt-16 flex items-center justify-center gap-3">
              {HERO_IMAGES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setHeroIndex(i)}
                  className="transition-all duration-500"
                  style={{ width: heroIndex === i ? '32px' : '8px', height: '3px', backgroundColor: heroIndex === i ? BRAND.gold : 'rgba(255,255,255,0.3)', border: 'none', borderRadius: '2px', cursor: 'pointer' }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
          <p className="text-white/50 text-xs tracking-widest uppercase mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>Explore</p>
          <svg className="w-5 h-5 text-white/50 mx-auto animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>

      {/* ==================== STATS / SOCIAL PROOF BAR ==================== */}
      <AnimateIn>
        <div style={{ backgroundColor: BRAND.dark }}>
          <div className="mx-auto max-w-7xl px-6 lg:px-8 py-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { number: '5.0', label: 'Google Rating', icon: Star },
                { number: '500+', label: 'Trips Planned', icon: Globe },
                { number: '150+', label: 'Countries', icon: MapPin },
                { number: '100%', label: 'Personal Service', icon: Heart },
              ].map(({ number, label, icon: Icon }) => (
                <div key={label} className="flex flex-col items-center gap-2">
                  <Icon className="h-5 w-5 mb-1" style={{ color: BRAND.gold }} />
                  <span
                    className="text-2xl sm:text-3xl font-light text-white"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {number}
                  </span>
                  <span
                    className="text-xs tracking-wider uppercase"
                    style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupeLight, fontWeight: 400 }}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </AnimateIn>

      {/* ==================== SERVICES RIBBON ==================== */}
      <div className="py-6 border-b" style={{ backgroundColor: BRAND.cream, borderColor: BRAND.taupeLight }}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Globe, label: 'Tailormade Itineraries' },
              { icon: Anchor, label: 'River & Ocean Cruises' },
              { icon: Plane, label: 'Luxury Holidays' },
              { icon: Shield, label: 'Travel Insurance' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center justify-center gap-3 py-2">
                <Icon className="h-5 w-5" style={{ color: BRAND.maroon }} />
                <span className="text-sm font-medium tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ==================== ABOUT / WELCOME SECTION ==================== */}
      <AnimateIn>
        <div className="py-24 sm:py-32" style={{ background: `linear-gradient(135deg, ${BRAND.maroon} 0%, ${BRAND.dark} 100%)` }}>
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-sm tracking-[0.3em] uppercase mb-4" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.gold, fontWeight: 500 }}>
                  Welcome to 2Life Travel
                </p>
                <h2 className="text-4xl sm:text-5xl font-light text-white leading-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Your Journey,{' '}
                  <span className="italic font-medium" style={{ color: BRAND.gold }}>Personally</span> Crafted
                </h2>
                <p className="text-base text-white/75 leading-relaxed mb-6" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  2Life Travel was founded by Adriaan Kruger in 2004. Born from a deep passion for travel and years of exploring the world's most incredible destinations, Adriaan and Sonia will transform your “wanderlust” into a reality that suits your budget. If you are tired of “cookie cutter” packed deals we will sit down and plan with you to realise your tailor made itinerary. We bring personal attention and expert knowledge to every adventure — whether you're dreaming of a luxury island escape, an African safari, or a cultural journey through Asia, Middle East, Far East, Europe or the Americas.
                </p>
                <p className="text-base text-white/75 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  Cruising, island hopping, safariing, touring, skiing, honeymooning, exploring or something completely unique — we'll help you plan and execute the holiday of a lifetime.
                </p>
                <div className="mt-8">
                  <a
                    href="/about"
                    className="inline-flex items-center gap-2 text-sm font-medium tracking-wider uppercase transition-all duration-300 border-b pb-1"
                    style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.gold, borderColor: BRAND.gold }}
                  >
                    Learn More <ChevronRight className="h-4 w-4" />
                  </a>
                </div>
              </div>

              {/* Image collage */}
              <div className="relative h-[480px] hidden lg:block">
                <img
                  src="https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&q=80"
                  alt="Zanzibar beach"
                  className="absolute top-0 left-0 w-72 h-48 object-cover shadow-2xl"
                  style={{ borderRadius: '2px' }}
                />
                <img
                  src="https://images.unsplash.com/photo-1516426122078-c23e76319801?w=600&q=80"
                  alt="Safari sunset"
                  className="absolute top-4 right-0 w-64 h-56 object-cover shadow-2xl"
                  style={{ borderRadius: '2px' }}
                />
                <img
                  src="https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=600&q=80"
                  alt="Bali temple"
                  className="absolute bottom-0 left-8 w-80 h-52 object-cover shadow-2xl"
                  style={{ borderRadius: '2px' }}
                />
                <img
                  src="https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=600&q=80"
                  alt="Maldives overwater villa"
                  className="absolute bottom-8 right-4 w-56 h-44 object-cover shadow-2xl"
                  style={{ borderRadius: '2px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </AnimateIn>

      {/* ==================== FEATURED PACKAGES ==================== */}
      <AnimateIn>
        <div id="offers" className="py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-sm tracking-[0.3em] uppercase mb-3" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}>
                Curated Experiences
              </p>
              <h2 className="text-4xl sm:text-5xl font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}>
                Featured <span className="italic font-medium" style={{ color: BRAND.maroon }}>Packages</span>
              </h2>
              <div className="mt-4 mx-auto w-16 h-0.5" style={{ backgroundColor: BRAND.gold }} />
              <p className="mt-6 text-base leading-7 max-w-2xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}>
                Discover our handpicked selection of exceptional travel experiences,
                each crafted for the discerning traveller.
              </p>
            </div>

            {packagesLoading ? (
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[4/3] rounded-sm bg-gray-200 mb-6"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
                    <div className="h-6 bg-gray-200 rounded w-2/3 mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  </div>
                ))}
              </div>
            ) : packagesError ? (
              <div className="text-center py-16">
                <h3 className="text-lg font-semibold text-gray-900">Unable to load packages</h3>
                <p className="mt-2 text-sm text-gray-600">{packagesError}</p>
              </div>
            ) : featuredPackages.length === 0 ? (
              <div className="text-center py-16">
                <h3 className="text-2xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}>
                  New packages coming soon
                </h3>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
                {featuredPackages.slice(0, 3).map((pkg, idx) => {
                  const imageUrl = getImageUrl(pkg)
                  return (
                    <AnimateIn key={pkg.id} delay={idx * 0.15}>
                      <article className="group">
                        <Link to={`/packages/${pkg.slug}`} className="block">
                          <div className="relative overflow-hidden" style={{ borderRadius: '2px' }}>
                            <div
                              className="aspect-[4/3] transition-transform duration-700 group-hover:scale-105"
                              style={{
                                backgroundColor: BRAND.taupe,
                                backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                              }}
                            >
                              {!imageUrl && (
                                <div className="flex items-center justify-center h-full">
                                  <Plane className="h-12 w-12 text-white opacity-40" />
                                </div>
                              )}
                            </div>
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500" />
                            {pkg.nights && (
                              <div
                                className="absolute top-4 right-4 px-3 py-1 text-xs font-medium tracking-wider uppercase text-white"
                                style={{ fontFamily: "'Montserrat', sans-serif", backgroundColor: BRAND.maroon, borderRadius: '2px' }}
                              >
                                {pkg.nights} {pkg.nights === 1 ? 'Night' : 'Nights'}
                              </div>
                            )}
                          </div>
                          <div className="mt-6">
                            {pkg.location && (
                              <p className="text-xs tracking-[0.2em] uppercase mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}>
                                {pkg.location}
                              </p>
                            )}
                            <h3 className="text-xl font-medium leading-snug group-hover:opacity-70 transition-opacity" style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}>
                              {pkg.name}
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed line-clamp-2" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}>
                              {pkg.intro || pkg.description}
                            </p>
                            <div className="mt-5 flex items-end justify-between pt-4" style={{ borderTop: `1px solid ${BRAND.cream}` }}>
                              <div>
                                <span className="text-xs" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe }}>From</span>
                                <span className="block text-2xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.maroon }}>
                                  {pkg.formatted_price}
                                </span>
                                <span className="text-xs" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe }}>per person sharing</span>
                              </div>
                              <span className="text-xs font-medium tracking-wider uppercase group-hover:translate-x-1 transition-transform inline-flex items-center gap-1" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.maroon }}>
                                Details <ChevronRight className="h-3 w-3" />
                              </span>
                            </div>
                          </div>
                        </Link>
                      </article>
                    </AnimateIn>
                  )
                })}
              </div>
            )}

            <div className="mt-16 text-center">
              <a href="/packages" className="inline-flex items-center gap-2 text-sm font-medium tracking-wider uppercase transition-all duration-300 border-b-2 pb-1" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.maroon, borderColor: BRAND.maroon }}>
                View all packages <ChevronRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </AnimateIn>

      {/* ==================== WHY CHOOSE US ==================== */}
      <AnimateIn>
        <div className="py-24 sm:py-32" style={{ backgroundColor: BRAND.cream }}>
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-sm tracking-[0.3em] uppercase mb-3" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}>
                Why Choose Us
              </p>
              <h2 className="text-4xl sm:text-5xl font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}>
                The 2Life Travel <span className="italic font-medium" style={{ color: BRAND.maroon }}>Difference</span>
              </h2>
              <div className="mt-4 mx-auto w-16 h-0.5" style={{ backgroundColor: BRAND.gold }} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {[
                { title: 'Tailormade Itineraries', desc: 'Every journey is uniquely crafted to match your dreams, preferences and budget. No two trips are alike.', icon: '✦' },
                { title: 'Expert Consultant', desc: 'Adriaan brings years of personal travel experience and industry knowledge to plan your perfect trip.', icon: '✦' },
                { title: 'Seamless Experience', desc: 'From planning to return, we ensure a stress-free, premium experience at every touchpoint.', icon: '✦' },
              ].map((item) => (
                <AnimateIn key={item.title} delay={0.1}>
                  <div className="text-center">
                    <div className="text-2xl mb-6" style={{ color: BRAND.gold }}>{item.icon}</div>
                    <h3 className="text-xl font-medium mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}>{item.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}>{item.desc}</p>
                  </div>
                </AnimateIn>
              ))}
            </div>
          </div>
        </div>
      </AnimateIn>

      {/* ==================== REVIEWS CAROUSEL ==================== */}
      <AnimateIn>
        <div className="py-24 sm:py-32 bg-white">
          <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
            <p className="text-sm tracking-[0.3em] uppercase mb-3" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}>
              Testimonials
            </p>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}>
              What Our <span className="italic font-medium" style={{ color: BRAND.maroon }}>Travellers</span> Say
            </h2>
            <div className="mt-4 mx-auto w-16 h-0.5" style={{ backgroundColor: BRAND.gold }} />

            <div className="relative mt-14">
              <button
                aria-label="Previous review"
                onClick={() => setActiveReview((prev) => (prev - 1 + REVIEWS.length) % REVIEWS.length)}
                className="absolute -left-2 md:-left-10 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center transition-colors"
                style={{ backgroundColor: BRAND.cream, borderRadius: '2px', color: BRAND.dark }}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>

              <div className="p-8 md:p-12" style={{ backgroundColor: BRAND.cream, borderRadius: '2px' }}>
                <div className="flex items-center justify-center gap-1 mb-6">
                  {Array.from({ length: REVIEWS[activeReview].rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4" style={{ color: BRAND.gold, fill: BRAND.gold }} />
                  ))}
                </div>
                <p
                  className="text-lg italic leading-relaxed max-w-2xl mx-auto"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark, fontSize: '20px' }}
                >
                  "{REVIEWS[activeReview].quote}"
                </p>
                <div className="mt-8">
                  <p className="text-sm font-medium" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}>{REVIEWS[activeReview].name}</p>
                  <p className="text-xs mt-1" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe }}>{REVIEWS[activeReview].date}</p>
                </div>
              </div>

              <button
                aria-label="Next review"
                onClick={() => setActiveReview((prev) => (prev + 1) % REVIEWS.length)}
                className="absolute -right-2 md:-right-10 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center transition-colors"
                style={{ backgroundColor: BRAND.cream, borderRadius: '2px', color: BRAND.dark }}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 flex items-center justify-center gap-2">
              {REVIEWS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveReview(idx)}
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: idx === activeReview ? '24px' : '8px',
                    backgroundColor: idx === activeReview ? BRAND.maroon : BRAND.taupeLight,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </AnimateIn>

      {/* ==================== eSIM SECTION ==================== */}
      <AnimateIn>
        <div
          className="relative py-24 sm:py-32 overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${BRAND.dark} 0%, #2A1520 50%, ${BRAND.dark} 100%)` }}
        >
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`, backgroundSize: '48px 48px' }} />
          </div>
          <div className="absolute top-0 left-0 w-full h-px" style={{ background: `linear-gradient(90deg, transparent, ${BRAND.gold}40, transparent)` }} />

          <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 mb-4">
                <Wifi className="h-4 w-4" style={{ color: BRAND.gold }} />
                <p className="text-sm tracking-[0.3em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.gold, fontWeight: 500 }}>Stay Connected</p>
              </div>
              <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Your 2Life Travel <span className="italic font-medium" style={{ color: BRAND.gold }}>eSIM</span>
              </h2>
              <div className="mt-4 mx-auto w-16 h-0.5" style={{ backgroundColor: BRAND.gold }} />
              <p className="mt-6 text-base leading-7 max-w-2xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupeLight, fontWeight: 300 }}>
                Travel without worrying about connectivity. Activate your eSIM and enjoy seamless data wherever your journey takes you.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
              <div className="order-2 lg:order-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {[
                    { icon: Globe, title: 'Global Connectivity', desc: 'Instant data access across 190+ countries worldwide' },
                    { icon: Shield, title: 'No Roaming Surprises', desc: 'Transparent pricing with no hidden fees or bill shock' },
                    { icon: Zap, title: 'Instant Activation', desc: 'Set up before you travel — works the moment you land' },
                    { icon: Smartphone, title: 'Seamless Travel', desc: 'Stay connected from departure to return, effortlessly' },
                  ].map(({ icon: Icon, title, desc }) => (
                    <div key={title} className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center" style={{ backgroundColor: `${BRAND.gold}15`, borderRadius: '2px', border: `1px solid ${BRAND.gold}25` }}>
                        <Icon className="h-5 w-5" style={{ color: BRAND.gold }} />
                      </div>
                      <div>
                        <h3 className="text-base font-medium text-white mb-1" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>{title}</h3>
                        <p className="text-sm leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupeLight, fontWeight: 300 }}>{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-10 pt-8 flex items-center gap-3" style={{ borderTop: `1px solid ${BRAND.gold}20` }}>
                  <p className="text-sm" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupeLight, fontWeight: 300 }}>
                    Need help?{' '}
                    <a href="mailto:info@2lifetravel.co.za" className="transition-opacity hover:opacity-80" style={{ color: BRAND.gold, fontWeight: 500 }}>
                      info@2lifetravel.co.za
                    </a>
                  </p>
                </div>
              </div>

              <div className="order-1 lg:order-2 flex justify-center">
                <div className="relative max-w-sm w-full text-center p-10" style={{ backgroundColor: '#FFFFFF', borderRadius: '2px', boxShadow: `0 25px 60px rgba(0,0,0,0.4)` }}>
                  <div className="absolute top-0 left-0 right-0 h-1" style={{ background: `linear-gradient(90deg, ${BRAND.maroon}, ${BRAND.gold}, ${BRAND.maroon})`, borderRadius: '2px 2px 0 0' }} />
                  <p className="text-xs tracking-[0.3em] uppercase mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}>Scan to activate</p>
                  <h3 className="text-2xl font-medium mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}>
                    Your Travel <span className="italic" style={{ color: BRAND.maroon }}>eSIM</span>
                  </h3>
                  <div className="inline-block p-5" style={{ backgroundColor: BRAND.cream, borderRadius: '2px', border: `1px solid ${BRAND.taupeLight}40` }}>
                    <img src="/esim-qr-code.png" alt="Scan this QR code to activate your 2Life Travel eSIM" className="w-48 h-48 sm:w-52 sm:h-52 mx-auto" style={{ imageRendering: 'crisp-edges' }} />
                  </div>
                  <p className="mt-6 text-sm" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}>Scan with your phone camera</p>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 w-full h-px" style={{ background: `linear-gradient(90deg, transparent, ${BRAND.gold}40, transparent)` }} />
        </div>
      </AnimateIn>

      {/* ==================== CTA + NEWSLETTER COMBINED ==================== */}
      <div
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${BRAND.dark} 0%, ${BRAND.maroon} 100%)` }}
      >
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`, backgroundSize: '40px 40px' }} />
        </div>
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <div className="pt-20 sm:pt-24 pb-12">
            <svg width="28" height="28" viewBox="0 0 40 40" className="mx-auto mb-6" style={{ opacity: 0.6 }}>
              <path d="M20 0 L22 16 L40 20 L22 22 L20 40 L18 22 L0 20 L18 16 Z" fill={BRAND.gold} />
            </svg>
            <h2 className="text-4xl sm:text-5xl font-light text-white leading-tight mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Ready to give your<br />
              <span className="italic font-medium" style={{ color: BRAND.gold }}>dreams</span> wings?
            </h2>
            <p className="text-base text-white/70 max-w-xl mx-auto mb-8 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              Let our expert consultants craft the perfect journey for you.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-3 px-10 py-4 text-sm font-medium tracking-wider uppercase transition-all duration-300 hover:scale-105"
              style={{ fontFamily: "'Montserrat', sans-serif", backgroundColor: BRAND.gold, color: BRAND.dark, borderRadius: '2px' }}
            >
              Start Planning Today <ChevronRight className="h-4 w-4" />
            </a>
          </div>
          <div className="mx-auto w-24 h-px" style={{ backgroundColor: `${BRAND.gold}40` }} />
          <div className="pt-12 pb-20 sm:pb-24">
            <h3 className="text-2xl sm:text-3xl font-light text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Stay in the <span className="italic font-medium" style={{ color: BRAND.gold }}>Loop</span>
            </h3>
            <p className="text-sm text-white/60 mb-6" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              Subscribe for travel inspiration and exclusive offers. No spam, ever.
            </p>
            <div className="flex w-full max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="min-w-0 flex-auto rounded-l-sm border-0 bg-white/10 backdrop-blur-sm px-4 py-3 text-white placeholder-white/40 text-sm focus:ring-2 focus:ring-inset focus:outline-none"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              />
              <button
                className="flex-none rounded-r-sm px-6 py-3 text-sm font-medium tracking-wider uppercase transition-all duration-300 hover:opacity-90"
                style={{ fontFamily: "'Montserrat', sans-serif", backgroundColor: BRAND.gold, color: BRAND.dark }}
              >
                Subscribe
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-px" style={{ background: `linear-gradient(90deg, transparent, ${BRAND.gold}50, transparent)` }} />
      </div>
    </div>
  )
}